import React, { useMemo, useState } from 'react';
import {
  Avatar,
  AvatarGroup,
  Box,
  Checkbox,
  Chip,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  ChevronRight as CollapsedIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandMore as ExpandedIcon,
} from '@mui/icons-material';
import { PRIORITIES } from './BoardTaskDialog';
import { isOverdue } from './KanbanBoard';

const INDENT = 28;

const initials = (name = '') => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('');

// Done / total over a task's whole subtree (excluding the task itself)
const subtreeProgress = (node) => {
  let total = 0;
  let done = 0;
  const walk = (n) => n.children.forEach((c) => { total += 1; if (c.task.is_done) done += 1; walk(c); });
  walk(node);
  return { total, done };
};

const TaskTree = ({
  lists, tasks, visibleTaskIds, currentUserId, canManage,
  onOpenTask, onAddSubtask, onToggleDone, onMoveToList, onDeleteTask,
}) => {
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [statusMenu, setStatusMenu] = useState(null); // { anchor, task }

  const listById = useMemo(() => Object.fromEntries(lists.map((l) => [l.id, l])), [lists]);

  // Build the forest. A task stays visible if it matches the filters or any
  // descendant does, so filtering never orphans a matching subtask.
  const roots = useMemo(() => {
    const listPos = Object.fromEntries(lists.map((l) => [l.id, l.position]));
    const nodes = Object.fromEntries(tasks.map((t) => [t.id, { task: t, children: [] }]));
    const top = [];
    tasks.forEach((t) => {
      if (t.parent && nodes[t.parent]) nodes[t.parent].children.push(nodes[t.id]);
      else top.push(nodes[t.id]);
    });
    const order = (a, b) =>
      (listPos[a.task.board_list] ?? 0) - (listPos[b.task.board_list] ?? 0) || a.task.position - b.task.position;
    const prune = (arr) => arr
      .map((n) => ({ ...n, children: prune(n.children) }))
      .filter((n) => visibleTaskIds.has(n.task.id) || n.children.length > 0)
      .sort(order);
    return prune(top);
  }, [tasks, lists, visibleTaskIds]);

  const toggleCollapsed = (id) => setCollapsed((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const renderNode = (node, depth) => {
    const { task } = node;
    const list = listById[task.board_list];
    const priority = PRIORITIES.find((p) => p.value === task.priority);
    const hasChildren = node.children.length > 0;
    const isCollapsed = collapsed.has(task.id);
    const progress = hasChildren ? subtreeProgress(node) : null;
    const canDelete = canManage || String(task.created_by) === String(currentUserId);
    const dimmed = !visibleTaskIds.has(task.id); // shown only as context for a matching subtask

    return (
      <React.Fragment key={task.id}>
        <Box
          sx={{
            display: 'flex', alignItems: 'center', gap: 1, py: 0.5, pr: 1,
            pl: `${depth * INDENT + 4}px`,
            borderBottom: 1, borderColor: 'divider',
            opacity: dimmed ? 0.55 : 1,
            '&:hover': { bgcolor: 'action.hover' },
            '&:hover .row-actions': { visibility: 'visible' },
          }}
        >
          <IconButton size="small" onClick={() => toggleCollapsed(task.id)} sx={{ visibility: hasChildren ? 'visible' : 'hidden' }}
            aria-label={isCollapsed ? 'Expand' : 'Collapse'}>
            {isCollapsed ? <CollapsedIcon fontSize="small" /> : <ExpandedIcon fontSize="small" />}
          </IconButton>
          <Checkbox size="small" checked={task.is_done} onChange={() => onToggleDone(task)} sx={{ p: 0.5 }} />
          <Box sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => onOpenTask(task)}>
            <Typography variant="body2" noWrap fontWeight={depth === 0 ? 600 : 400}
              sx={{ textDecoration: task.is_done ? 'line-through' : 'none', color: task.is_done ? 'text.secondary' : 'text.primary' }}>
              {task.title}
            </Typography>
            {progress && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, maxWidth: 220 }}>
                <LinearProgress variant="determinate" value={(progress.done / progress.total) * 100} sx={{ flex: 1, height: 4, borderRadius: 2 }} />
                <Typography variant="caption" color="text.secondary">{progress.done}/{progress.total}</Typography>
              </Box>
            )}
          </Box>

          <Chip
            size="small"
            label={list?.name || '—'}
            color={list?.is_done_list ? 'success' : 'default'}
            onClick={(e) => setStatusMenu({ anchor: e.currentTarget, task })}
            sx={{ height: 22, minWidth: 80 }}
          />
          {priority && (
            <Chip size="small" label={priority.label} color={priority.color} variant="outlined" sx={{ height: 22, width: 70, display: { xs: 'none', sm: 'flex' } }} />
          )}
          <Typography variant="caption" sx={{ width: 84, textAlign: 'right', display: { xs: 'none', md: 'block' } }}
            color={isOverdue(task) ? 'error' : 'text.secondary'}>
            {task.due_date ? new Date(task.due_date).toLocaleDateString() : ''}
          </Typography>
          <Box sx={{ width: 76, display: { xs: 'none', sm: 'flex' }, justifyContent: 'flex-end' }}>
            <AvatarGroup max={3} sx={{ '& .MuiAvatar-root': { width: 22, height: 22, fontSize: 10 } }}>
              {(task.assignee_names || []).map((a) => (
                <Tooltip key={a.id} title={a.name}><Avatar>{initials(a.name)}</Avatar></Tooltip>
              ))}
            </AvatarGroup>
          </Box>
          <Box className="row-actions" sx={{ visibility: 'hidden', display: 'flex' }}>
            <Tooltip title="Add subtask">
              <IconButton size="small" onClick={() => onAddSubtask(task)}><AddIcon fontSize="small" /></IconButton>
            </Tooltip>
            <Tooltip title="Edit">
              <IconButton size="small" onClick={() => onOpenTask(task)}><EditIcon fontSize="small" /></IconButton>
            </Tooltip>
            {canDelete && (
              <Tooltip title="Delete">
                <IconButton size="small" onClick={() => onDeleteTask(task)}><DeleteIcon fontSize="small" /></IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
        {hasChildren && !isCollapsed && node.children.map((child) => renderNode(child, depth + 1))}
      </React.Fragment>
    );
  };

  return (
    <Paper variant="outlined">
      {roots.length === 0 ? (
        <Typography color="text.secondary" sx={{ p: 4, textAlign: 'center' }}>
          No tasks yet. Use “Add task” to create the first one.
        </Typography>
      ) : roots.map((node) => renderNode(node, 0))}

      <Menu anchorEl={statusMenu?.anchor} open={Boolean(statusMenu)} onClose={() => setStatusMenu(null)} sx={{ zIndex: 10001 }}>
        {lists.map((l) => (
          <MenuItem
            key={l.id}
            selected={statusMenu?.task.board_list === l.id}
            onClick={() => { onMoveToList(statusMenu.task, l.id); setStatusMenu(null); }}
          >
            {l.name}
          </MenuItem>
        ))}
      </Menu>
    </Paper>
  );
};

export default TaskTree;
